import type { Tracker } from '@/entities/tracker';

export type TrackerOwnership = 'all' | 'mine' | 'shared';

/** A hub card's tracker, with the workspace it sits in (null when it has none). */
export type HubTracker = Tracker & { workspaceId: string | null; workspaceName: string };

/** A default board's recorded creator only triggered its provisioning, so it is nobody's own. */
export function isOwnTracker(
  tracker: Pick<Tracker, 'createdBy' | 'defaultKey'>,
  currentUserId: string | null,
): boolean {
  return !tracker.defaultKey && currentUserId !== null && tracker.createdBy === currentUserId;
}

export function filterTrackers<T extends Tracker & { workspaceId?: string | null }>(
  trackers: T[],
  options: {
    ownership: TrackerOwnership;
    currentUserId: string | null;
    search: string;
    sortId: string | null;
    /** Null for every workspace. */
    workspaceId?: string | null;
  },
): T[] {
  const { ownership, currentUserId, search, sortId, workspaceId = null } = options;
  const isMine = (t: Tracker) => isOwnTracker(t, currentUserId);
  const owned = (
    ownership === 'mine'
      ? trackers.filter(isMine)
      : ownership === 'shared'
        ? trackers.filter((t) => !isMine(t))
        : trackers
  ).filter((t) => !workspaceId || t.workspaceId === workspaceId);

  const query = search.trim().toLowerCase();
  const matched = query
    ? owned.filter(
        (t) =>
          t.name.toLowerCase().includes(query) ||
          (t.description ?? '').toLowerCase().includes(query),
      )
    : owned;

  // No sort chosen: keep backend order (oldest first), so the starter board leads.
  if (!sortId) return matched;
  const sorted = [...matched];
  switch (sortId) {
    case 'createdAt-desc':
      sorted.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
      break;
    case 'createdAt-asc':
      sorted.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      break;
    case 'name-asc':
      sorted.sort((a, b) => a.name.localeCompare(b.name));
      break;
    case 'name-desc':
      sorted.sort((a, b) => b.name.localeCompare(a.name));
      break;
  }
  return sorted;
}
